import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, getAppSettings } from '../api';
import {
  Text,
  Heading,
  HStack,
  Button,
  Box,
  SimpleGrid,
  VStack
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  TimeIcon,
  HamburgerIcon,
  AtSignIcon,
  ViewIcon,
  StarIcon,
  RepeatIcon
} from '@chakra-ui/icons';

import Download from './Download';
import Operator from './Operator';
import Promoter from './Promoter';
import Cashier from './Cashier';
import EmailInput from './EmailInput';
import OrderIdInput from './OrderIdInput';

import Login from '../components/Login';
import Logout from '../components/Logout';
import Footer from '../components/Footer';

import { useAppSettings } from '../contexts/app-settings-ctx';

const cardStyle = {
  border: '3px solid',
  borderColor: 'teal',
  borderRadius: 15,
  p: 5,
  color: 'white',
  h: 'full'
};

const CardLink = ({ to, title, icon: Icon, isDisabled }) => (
  <Box {...cardStyle}>
    <VStack align="stretch" spacing={4}>
      {Icon && <Box><Icon boxSize={6} /></Box>}
      {isDisabled ? (
        <Button size="lg" isDisabled>
          {title}
        </Button>
      ) : (
        <Button as={Link} to={to} size="lg">
          {title}
        </Button>
      )}
    </VStack>
  </Box>
);

CardLink.propTypes = {
  to: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  isDisabled: PropTypes.bool
};

const Dashboard = () => {
  const [user] = useAuthState(auth);
  const { appSettings, setAppSettings } = useAppSettings();
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const settings = await getAppSettings();
        setAppSettings(settings);
      } catch (error) {
        console.error('Errore nel caricamento delle impostazioni applicative', error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    if (appSettings?.adminCashier === undefined) {
      loadSettings();
    }
  }, [appSettings, setAppSettings]);

  if (!user) return <Login />;

  if (user.displayName === 'operator') {
    return <Operator />;
  }

  if (user.displayName === 'promoter') {
    return <Promoter />;
  }

  if (user.displayName === 'cashier') {
    return <Cashier />;
  }

  return (
    <>
      <Heading mt={5} mb={1} color="white" textAlign="center" size="lg">
        Admin Dashboard
      </Heading>
      <Text mb={6} color="white" textAlign="center" fontSize="sm">
        {process.env.REACT_APP_PROJECT_ID}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={10}>
        <CardLink
          to="/dashboard/event-dates"
          title="Gestione date evento"
          icon={CalendarIcon}
        />
        <CardLink
          to="/dashboard/realtime"
          title="Ordini in tempo reale"
          icon={TimeIcon}
        />

        {appSettings?.hasMenuEditor === 1 ? (
          <CardLink
            to="/dashboard/menu-admin"
            title="Gestione menù"
            icon={HamburgerIcon}
          />
        ) : null}


        {appSettings?.hasUsersAdmin === 1 ? (
          <CardLink
            to="/dashboard/users"
            title="Gestione Utenze"
            icon={AtSignIcon}
          />
        ) : null}

        <CardLink
          to="/dashboard/voucher"
          title="Crea Voucher"
          icon={StarIcon}
        />
        <CardLink
          to="/dashboard/operators-stats"
          title="Statistiche operatori"
          icon={ViewIcon}
        />


        {appSettings?.adminCashier ? (
          <CardLink
            to="/menu"
            title="Modalità Cassa"
            icon={RepeatIcon}
            isDisabled={isLoadingSettings}
          />
        ) : null}

        {appSettings?.cashierOrderHistory ? (
          <CardLink
            to="/dashboard/cashier-monitor"
            title="Monitor Casse"
            icon={ViewIcon}
            isDisabled={isLoadingSettings}
          />
        ) : null}


        {appSettings?.hasWarehouse ? (
          <CardLink
            to="/dashboard/warehouse"
            title="Gestore Magazzino"
            icon={ViewIcon}
            isDisabled={isLoadingSettings}
          />
        ) : null}

      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={10}>
        <Box {...cardStyle}>
          <Text fontSize="xl" fontWeight={600} mb={5}>
            Cerca ordini per email
          </Text>

          <EmailInput />
        </Box>

        <Box {...cardStyle}>
          <Text fontSize="xl" fontWeight={600} mb={5}>
            Cerca ordine per Id
          </Text>

          <OrderIdInput />
        </Box>
      </SimpleGrid>
      <Download />


      <HStack justify="center" spacing={4} flexWrap="wrap">
        <Text color="white" my={5}>
          Benvenuto {user.email}
        </Text>
        <Logout />
      </HStack>
      <Footer />
    </>
  );
};

export default Dashboard;
